import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
  type TooltipProps,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";
import { APP_FONT_FAMILY } from "../../constants/fonts";

type SetupCoachmarkProps = {
  open: boolean;
  title: string;
  subtitle: string;
  onDismiss: () => void;
  onNext?: () => void;
  nextLabel?: string;
  /** Optional actions rendered inside the tip (e.g. language shortcut). */
  tipActions?: ReactNode;
  fullWidth?: boolean;
  /**
   * `menu` — compact target styles for MuiMenu items (still dims the screen).
   * `sidebar` | `default` — full-screen portal dim with a lit cutout on the target.
   */
  variant?: "default" | "menu" | "sidebar";
  /** Override tooltip placement (defaults keep tips on-screen). */
  placement?: TooltipProps["placement"];
  /**
   * No outline/glow on the target — use for dialogs/menus that should stay
   * readable against a dimmed backdrop without field highlighting.
   */
  flat?: boolean;
  /** Spotlight hole corner radius (px). */
  holeRadius?: number;
  children: ReactElement;
};

type Hole = { top: number; left: number; width: number; height: number };

function ScreenDimOverlay({
  hole,
  dim,
  zIndex,
  radius = 14,
}: {
  hole: Hole;
  dim: string;
  zIndex: number;
  radius?: number;
}) {
  return createPortal(
    <>
      {/* Dim everywhere except the hole (transparent box + huge shadow) */}
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: hole.top,
          left: hole.left,
          width: hole.width,
          height: hole.height,
          borderRadius: `${radius}px`,
          boxShadow: `0 0 0 9999px ${dim}`,
          zIndex,
          pointerEvents: "none",
        }}
      />
      {/* Block clicks on dimmed regions */}
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: Math.max(0, hole.top),
          zIndex,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: hole.top + hole.height,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: hole.top,
          left: 0,
          width: Math.max(0, hole.left),
          height: hole.height,
          zIndex,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: hole.top,
          left: hole.left + hole.width,
          right: 0,
          height: hole.height,
          zIndex,
        }}
      />
    </>,
    document.body,
  );
}

/**
 * Spotlight onboarding hint: dims the whole screen via a portal cutout,
 * keeps the target lit, and shows a coachmark tip.
 */
export function SetupCoachmark({
  open,
  title,
  subtitle,
  onDismiss,
  onNext,
  nextLabel,
  tipActions,
  fullWidth = false,
  variant = "default",
  placement: placementProp,
  flat = false,
  holeRadius,
  children,
}: SetupCoachmarkProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dim = alpha("#0f172a", 0.72);
  const isMenu = variant === "menu";
  // Always dim the screen when a tip is open — including MuiMenu items.
  const usePortalDim = true;
  const targetRef = useRef<HTMLDivElement>(null);
  const [hole, setHole] = useState<Hole | null>(null);

  useLayoutEffect(() => {
    if (!open || !usePortalDim) {
      setHole(null);
      return;
    }

    const measure = () => {
      const el = targetRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      const pad = flat ? 0 : variant === "sidebar" ? 6 : 8;
      setHole({
        top: r.top - pad,
        left: r.left - pad,
        width: r.width + pad * 2,
        height: r.height + pad * 2,
      });
    };

    measure();
    const raf = window.requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    const timer = window.setInterval(measure, 200);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      window.clearInterval(timer);
    };
  }, [open, usePortalDim, variant, flat]);

  const tip = (
    <Box sx={{ position: "relative", textAlign: "center", pt: 0.5 }}>
      <IconButton
        aria-label={t("common.close")}
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        sx={{
          position: "absolute",
          top: -10,
          insetInlineEnd: -10,
          color: "text.disabled",
          p: 0.5,
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Typography
        sx={{
          fontFamily: APP_FONT_FAMILY,
          fontSize: "0.9375rem",
          fontWeight: 600,
          lineHeight: 1.35,
          color: "text.primary",
          mb: 0.5,
          px: 2.5,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: APP_FONT_FAMILY,
          fontSize: "0.8125rem",
          fontWeight: 400,
          lineHeight: 1.45,
          color: "text.secondary",
          mb: tipActions || onNext ? 1.5 : 0,
          px: 1,
        }}
      >
        {subtitle}
      </Typography>
      {tipActions ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 1,
            mb: onNext ? 1.25 : 0,
          }}
        >
          {tipActions}
        </Box>
      ) : null}
      {onNext ? (
        <Button
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          sx={{ textTransform: "none", borderRadius: "10px", px: 2 }}
        >
          {nextLabel ?? t("selfSetup.tourNext")}
        </Button>
      ) : null}
    </Box>
  );

  // Page content: prefer bottom so wide/fullWidth targets don't push tips off-screen.
  // Sidebar/menu: tip to the right of the nav item (or top on mobile).
  const placement =
    placementProp ??
    (isMenu || variant === "sidebar"
      ? isMobile
        ? "top"
        : "right"
      : "bottom");

  const tooltipSlotProps = {
    popper: {
      // Above MuiMenu / Dialog paper and the spotlight dim layers.
      sx: { zIndex: (t: { zIndex: { modal: number } }) => t.zIndex.modal + 20 },
      modifiers: [
        {
          name: "flip",
          options: {
            // Prefer side placements so tips don't jump above the target
            // unless the tip explicitly requested `placement="top"`.
            fallbackPlacements:
              placement === "top"
                ? ["bottom", "right", "left"]
                : ["right", "left", "bottom"],
          },
        },
        {
          name: "preventOverflow",
          options: {
            padding: 12,
            altAxis: true,
            boundary: "viewport",
          },
        },
      ],
    },
    tooltip: {
      sx: {
        maxWidth: isMobile ? "calc(100vw - 32px)" : 420,
        width: "max-content",
        px: 2.5,
        py: 1.75,
        bgcolor: "background.paper",
        color: "text.primary",
        // Match dashboard StatCard/ChartCard radius (avoid theme multiplier).
        borderRadius: "14px",
        border: 1,
        borderColor: "divider",
        boxShadow: "0 10px 32px rgba(15, 23, 42, 0.22)",
      },
    },
    arrow: {
      sx: {
        color: "background.paper",
        "&::before": {
          border: `1px solid ${theme.palette.divider}`,
          boxSizing: "border-box",
        },
      },
    },
  };

  const dimZ = theme.zIndex.modal + 5;
  const resolvedHoleRadius =
    holeRadius ?? (flat ? 16 : variant === "sidebar" ? 12 : 14);

  // Always keep the same Tooltip > Box tree so Menu anchors / children are not
  // remounted when a tip opens or closes (avoids popovers jumping to top-left).
  return (
    <>
      {open && usePortalDim && hole ? (
        <ScreenDimOverlay
          hole={hole}
          dim={dim}
          zIndex={dimZ}
          radius={resolvedHoleRadius}
        />
      ) : null}
      <Tooltip
        open={open}
        arrow={open}
        placement={placement}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        title={open ? tip : ""}
        TransitionProps={{ timeout: 0 }}
        slotProps={open ? tooltipSlotProps : undefined}
      >
        <Box
          ref={targetRef}
          sx={{
            display:
              fullWidth || isMenu || variant === "sidebar" ? "block" : "inline-flex",
            width:
              fullWidth || isMenu || variant === "sidebar" ? "100%" : "auto",
            maxWidth: "100%",
            borderRadius: !open || flat || isMenu ? 0 : "14px",
            position: "relative",
            zIndex: open ? 2 : "auto",
            p:
              !open || flat
                ? 0
                : isMenu || usePortalDim
                  ? variant === "default" && !fullWidth
                    ? 1.25
                    : 0
                  : 1.25,
            bgcolor:
              !open || flat
                ? "transparent"
                : isMenu
                  ? alpha(theme.palette.primary.main, 0.08)
                  : "background.paper",
            outline:
              !open || flat
                ? "none"
                : isMenu
                  ? `2px solid ${theme.palette.primary.main}`
                  : `3px solid ${theme.palette.common.white}`,
            outlineOffset: !open || flat ? 0 : isMenu ? "-2px" : "2px",
            boxShadow:
              !open || flat || isMenu
                ? "none"
                : `0 8px 28px ${alpha(theme.palette.primary.main, 0.35)}`,
          }}
        >
          {children}
        </Box>
      </Tooltip>
    </>
  );
}
